import { Link } from 'react-router-dom';
import { useState } from 'react';
import { useSEO, generateBreadcrumbSchema, generateLocalBusinessSchema, generateWebPageSchema } from '../../utils/seo';

export default function Iletisim() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    course: '',
    message: '',
    company: '',
    participantCount: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'success' | 'error' | null>(null);

  // SEO Configuration
  useSEO({
    title: 'İletişim | ASSA Akademi Osmaniye - Adres, Telefon, Kayıt Bilgileri',
    description: 'ASSA Akademi Osmaniye iletişim: Adnan Menderes, Emniyet Cd. 28/B, Osmaniye. ☎ 0850 346 2772. İlkyardım eğitimi, yüksekte çalışma ve iş güvenliği kursları için hemen kayıt olun.',
    keywords: 'ASSA Akademi iletişim, Osmaniye ilkyardım eğitimi kayıt, iş güvenliği eğitimi telefon, Osmaniye eğitim merkezi adres, ilkyardım kursu kayıt',
    canonical: '/iletisim',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        generateBreadcrumbSchema([
          { name: 'Ana Sayfa', url: '/' },
          { name: 'İletişim', url: '/iletisim' }
        ]),
        generateWebPageSchema('İletişim', 'ASSA İş Eğitimleri Akademisi Osmaniye iletişim bilgileri ve kayıt formu', '/iletisim'),
        generateLocalBusinessSchema()
      ]
    }
  });

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const formPayload = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formPayload.append(key, value);
      });

      const response = await fetch(
        'https://readdy.ai/api/form/d38ehc3tg2u3f2oj4df0',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded'
          },
          body: formPayload
        }
      );

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          course: '',
          message: '',
          company: '',
          participantCount: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="bg-white shadow-lg sticky top-0 z-50">
        <div className="container mx-auto px-6">
          <div className="flex justify-between items-center py-4">
            <Link to="/" className="flex items-center">
              <img 
                src="https://static.readdy.ai/image/69983b9b992309f9f345f22b715baa7c/d976ad48ea6e824eb2b305836503c616.jfif" 
                alt="ASSA Logo" 
                className="h-12 w-auto"
              />
            </Link>
            <div className="hidden md:flex space-x-8">
              <Link to="/" className="text-gray-700 hover:text-blue-600 cursor-pointer">Ana Sayfa</Link>
              <Link to="/egitimlerimiz" className="text-gray-700 hover:text-blue-600 cursor-pointer">Eğitimlerimiz</Link>
              <Link to="/hakkimizda" className="text-gray-700 hover:text-blue-600 cursor-pointer">Hakkımızda</Link>
              <Link to="/sss" className="text-gray-700 hover:text-blue-600 cursor-pointer">SSS</Link>
              <Link to="/iletisim" className="text-blue-600 font-semibold cursor-pointer">İletişim</Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="relative h-[60vh] bg-cover bg-center bg-no-repeat flex items-center"
        style={{
          backgroundImage:
            'linear-gradient(rgba(37, 99, 235, 0.85), rgba(37, 99, 235, 0.7)), url("https://public.readdy.ai/ai/img_res/9c87a08a743adab6eef86e07798b806a.jpg")'
        }}
      >
        <div className="container mx-auto px-6 text-white">
          <div className="max-w-3xl">
            <h1 className="text-5xl font-bold mb-6">İletişim</h1>
            <p className="text-xl mb-8 leading-relaxed">
              Sorularınız için bizimle iletişime geçin. Uzman ekibimiz size yardımcı
              olmaktan mutluluk duyar.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold text-gray-800 mb-8">
                İletişim Bilgileri
              </h2>

              <div className="space-y-8">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-map-pin-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Adresimiz
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      Adnan Menderes, Emniyet Cd. 28/B
                      <br />
                      80010 Osmaniye Merkez/Osmaniye
                    </p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-green-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-phone-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Telefon
                    </h3>
                    <p className="text-gray-600">0850 346 2772</p>
                    <p className="text-gray-600">+90 532 419 64 01 (WhatsApp)</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-mail-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      E-posta
                    </h3>
                    <p className="text-gray-600">info@assaakademi.com.tr</p>
                    <p className="text-gray-600">kayit@assaakademi.com.tr</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-purple-600 rounded-full flex items-center justify-center mr-4 mt-1">
                    <i className="ri-time-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-800 mb-2">
                      Çalışma Saatleri
                    </h3>
                    <p className="text-gray-600">
                      Pazartesi - Cuma: 09:00 - 18:00
                    </p>
                    <p className="text-gray-600">
                      Cumartesi: 09:00 - 14:00
                    </p>
                    <p className="text-gray-600">Pazar: Kapalı</p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="mt-12">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">
                  Sosyal Medya
                </h3>
                <div className="flex space-x-4">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-700 transition-colors">
                    <i className="ri-facebook-fill text-white"></i>
                  </div>
                  <div className="w-12 h-12 bg-blue-400 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-500 transition-colors">
                    <i className="ri-twitter-fill text-white"></i>
                  </div>
                  <div className="w-12 h-12 bg-blue-700 rounded-full flex items-center justify-center cursor-pointer hover:bg-blue-800 transition-colors">
                    <i className="ri-linkedin-fill text-white"></i>
                  </div>
                  <div className="w-12 h-12 bg-red-600 rounded-full flex items-center justify-center cursor-pointer hover:bg-red-700 transition-colors">
                    <i className="ri-instagram-line text-white"></i>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="bg-white p-8 rounded-xl shadow-lg">
              <h2 className="text-3xl font-bold text-gray-800 mb-8">
                İletişim Formu
              </h2>

              {submitStatus === 'success' && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg mb-6">
                  <div className="flex items-center">
                    <i className="ri-check-line mr-2"></i>
                    <span>
                      Mesajınız başarıyla gönderildi. En kısa sürede size dönüş
                      yapacağız.
                    </span>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
                  <div className="flex items-center">
                    <i className="ri-error-warning-line mr-2"></i>
                    <span>
                      Mesaj gönderilirken bir hata oluştu. Lütfen tekrar deneyin.
                    </span>
                  </div>
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                id="iletisim-formu"
              >
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Ad Soyad *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Adınız ve soyadınız"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      E-posta *
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="ornek@email.com"
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Telefon *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="0555 123 45 67"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="course"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      İlgilendiğiniz Eğitim
                    </label>
                    <div className="relative">
                      <select
                        id="course"
                        name="course"
                        value={formData.course}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm pr-8 appearance-none cursor-pointer"
                      >
                        <option value="">Seçiniz</option>
                        <option value="temel-ilkyardim">Temel İlkyardım Eğitimi</option>
                        <option value="guncelleme">Temel İlkyardım Güncelleme Eğitimi</option>
                        <option value="egitici">İlk Yardım Eğitici Eğitimi</option>
                        <option value="yuksekte-calisma">Yüksekte Çalışma Eğitimi</option>
                        <option value="yangin">Yangın Eğitimi</option>
                        <option value="hijyen">Hijyen Eğitimi</option>
                        <option value="mesleki">Mesleki Eğitimler</option>
                        <option value="myk">MYK Danışmanlığı</option>
                      </select>
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <i className="ri-arrow-down-s-line text-gray-400"></i>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label
                      htmlFor="company"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Firma Adı (Opsiyonel)
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="Firma adınız"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="participantCount"
                      className="block text-sm font-semibold text-gray-700 mb-2"
                    >
                      Katılımcı Sayısı
                    </label>
                    <input
                      type="number"
                      id="participantCount"
                      name="participantCount"
                      value={formData.participantCount}
                      onChange={handleInputChange}
                      min="1"
                      max="100"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      placeholder="1"
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label
                    htmlFor="message"
                    className="block text-sm font-semibold text-gray-700 mb-2"
                  >
                    Mesajınız *
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm resize-vertical"
                    placeholder="Sorularınızı ve detayları buraya yazabilirsiniz..."
                  ></textarea>
                  <div className="text-right text-sm text-gray-500 mt-1">
                    {formData.message.length}/500 karakter
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer flex items-center justify-center"
                >
                  {isSubmitting ? (
                    <>
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      Gönderiliyor...
                    </>
                  ) : (
                    <>
                      <i className="ri-send-plane-line mr-2"></i>
                      Mesaj Gönder
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-800 mb-6">Konum</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Osmaniye Merkez
            </p>
          </div>

          <div className="rounded-xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3196.7865421563977!2d36.26146567536795!3d37.07425654098975!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x152f7b5b6c8e5e6b%3A0x7e8b5a5c5d5e5f60!2sAdnan%20Menderes%2C%20Emniyet%20Cd.%20No%3A28%2FB%2C%2080010%20Merkez%2FOsmaniye!5e0!3m2!1str!2str!4v1640995200000!5m2!1str!2str"
              width="100%"
              height="450"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Assa İş Eğitimleri Akademisi Konum"
            ></iframe>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="container mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold mb-6">Hemen Başlayın</h2>
          <p className="text-xl mb-8 max-w-3xl mx-auto leading-relaxed">
            İlkyardım eğitimi almak için bugün bizimle iletişime geçin. Uzman
            kadromuz size en uygun eğitim programını önerecektir.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/egitimlerimiz"
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Eğitimleri İncele
            </Link>
            <Link
              to="/hakkimizda"
              className="bg-transparent border-2 border-white hover:bg-white hover:text-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold transition-colors whitespace-nowrap cursor-pointer"
            >
              Hakkımızda
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-800 text-white py-16">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <p className="text-gray-300 leading-relaxed mb-6">
                Sağlık Bakanlığı onaylı ilkyardım eğitimleri ile hayat kurtarmayı öğrenin.
              </p>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Hızlı Linkler</h4>
              <ul className="space-y-3">
                <li>
                  <Link to="/" className="text-gray-300 hover:text-white cursor-pointer">
                    Ana Sayfa
                  </Link>
                </li>
                <li>
                  <Link to="/egitimlerimiz" className="text-gray-300 hover:text-white cursor-pointer">
                    Eğitimlerimiz
                  </Link>
                </li>
                <li>
                  <Link to="/hakkimizda" className="text-gray-300 hover:text-white cursor-pointer">
                    Hakkımızda
                  </Link>
                </li>
                <li>
                  <Link to="/iletisim" className="text-gray-300 hover:text-white cursor-pointer">
                    İletişim
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">Eğitimler</h4>
              <ul className="space-y-3">
                <li>
                  <span className="text-gray-300">İSG Temel Eğitimi</span>
                </li>
                <li>
                  <span className="text-gray-300">Yüksekte Çalışma</span>
                </li>
                <li>
                  <span className="text-gray-300">Yangın Eğitimi</span>
                </li>
                <li>
                  <span className="text-gray-300">İlkyardım Eğitimi</span>
                </li>
              </ul>
            </div>

            <div>
              <h4 className="text-lg font-semibold mb-6">İletişim</h4>
              <div className="space-y-3">
                <div className="flex items-center">
                  <i className="ri-phone-line text-blue-400 mr-3"></i>
                  <span className="text-gray-300">0850 346 2772</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-mail-line text-blue-400 mr-3"></i>
                  <span className="text-gray-300">info@assaakademi.com.tr</span>
                </div>
                <div className="flex items-start">
                  <i className="ri-map-pin-line text-blue-400 mr-3 mt-1"></i>
                  <span className="text-gray-300">Adnan Menderes, Emniyet Cd. 28/B, 80010 Osmaniye Merkez/Osmaniye</span>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-gray-700 pt-8 mt-12">
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-gray-400 mb-4 md:mb-0">
                © 2024 Assa İş Eğitimleri Akademisi. Tüm hakları saklıdır.
              </p>
            </div>
          </div>
        </div>
      </footer>

      {/* WhatsApp Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/+905324196401"
          target="_blank"
          rel="noopener noreferrer"
          className="bg-green-500 hover:bg-green-600 text-white w-16 h-16 rounded-full flex items-center justify-center shadow-lg hover:shadow-xl transition-all duration-300 animate-pulse cursor-pointer"
          title="WhatsApp ile iletişime geç"
        >
          <i className="ri-whatsapp-line text-2xl"></i>
        </a>
      </div>
    </div>
  );
}
